import {ILanguageService} from "./language.service.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {ServiceBase} from "../service-base";
import {IServiceFactory} from "../service-factory.interface";
import {Locale} from "date-fns";
import {de, el, enGB, es, fr, it, ro} from 'date-fns/locale';
import {TranslationWithParams} from "./translation-with-params";
import {AirlineWebapiErrorCodesEnum} from "../airline-webapi/airline-webapi-error-codes.enum";
import {WellKnownQueryParams} from "../window/window.service.interface";

interface ILanguageInfo {
    locale: Locale;
    languageCode: string;
}

const DEFAULT_LANGUAGE = 'en-gb';
export class LanguageService extends ServiceBase implements ILanguageService {
    constructor(services: IServiceFactory) {
        super(services);
        this._currentLanguageInfo = this._getCurrentLanguageFromUrl();
        this._loadTexts(this.currentLanguage);
        makeObservable(this, {
            _currentLanguageInfo: observable.ref,
            _texts: observable.ref
        });
    }

    private _getCurrentLanguageFromUrl(): ILanguageInfo {
        const langParamValue = this.services.window.getUrlParam(WellKnownQueryParams.Lang)?.toLowerCase();
        if(!langParamValue) {
            return this._getLanguageInfoByLanguageCode(DEFAULT_LANGUAGE);
        }

        return this._getLanguageInfoByLanguageCode(langParamValue);
    }

    private _getLanguageInfoByLanguageCode(languageCode: string): ILanguageInfo {
        switch (languageCode?.toLowerCase()) {
            case 'de-de':
                return {
                    locale: de,
                    languageCode: 'de-DE'
                };
            case 'el-gr':
                return {
                    locale: el,
                    languageCode: 'el-GR'
                };
            case 'es-es':
                return {
                    locale: es,
                    languageCode: 'es-ES'
                };
            case 'en-gb':
                return {
                    locale: enGB,
                    languageCode: 'en-GB'
                };
            case 'fr-fr':
                return {
                    locale: fr,
                    languageCode: 'fr-FR'
                };
            case 'it-it':
                return {
                    locale: it,
                    languageCode: 'it-IT'
                };
            case 'ro-ro':
                return {
                    locale: ro,
                    languageCode: 'ro-RO'
                };
            default:
                return {
                    locale: enGB,
                    languageCode: 'en-GB'
                };
        }
    }

    _currentLanguageInfo: ILanguageInfo;
    get currentLanguage(): string {
        return this._currentLanguageInfo.languageCode;
    }

    get currentLanguageIso2(): string {
        return this.currentLanguage.split('-')[0];
    }

    get currentLocale(): any {
        return this._currentLanguageInfo.locale;
    }

    private _loadTexts(languageCode: string): void {
        if(languageCode.toLowerCase() === DEFAULT_LANGUAGE) {
            this._setTexts({});
            return;
        }

        import(`./translations/texts/texts.${languageCode}.ts`).then(module => {
            this._setTexts(module.default);
        }).catch((err) => {
            this.services.logger.error(`Failed to load texts for language ${languageCode}`, err);
            this._setTexts({});
        });
    }

    _texts: Record<string, string> = {};
    get texts(): Record<string, string> {
        return this._texts;

    }

    private _setTexts(texts: Record<string, string>): void {
        runInAction(() => {
            this._texts = texts;
        });
    }

    translate(template: string): string {
        if(this.texts[template]) {
            return this.texts[template];
        } else {
            return template;
        }
    }

    translationFor(template: string): TranslationWithParams {
        if(this.texts[template]) {
            template = this.texts[template];
        }
        return new TranslationWithParams(template);
    }

    translateApiError(errorCode: AirlineWebapiErrorCodesEnum): string {
        switch (errorCode) {
            case AirlineWebapiErrorCodesEnum.InvalidCredentials:
                return this.translate('Invalid user name or password');
            case AirlineWebapiErrorCodesEnum.AccountLockedOut:
                return this.translate('Your account is locked. Please check your e-mail and confirm your registration before proceed with login');
            case AirlineWebapiErrorCodesEnum.UserAlreadyExists:
                return this.translate('User already exists');
            case AirlineWebapiErrorCodesEnum.PasswordAlreadyUsedInTheLast5Password:
                return this.translate('The password requested was used in the last 5 passwords.Please select a different password');
            case AirlineWebapiErrorCodesEnum.UserDoesntExist:
                return this.translate('This user name does not exist');
            default:
                return this.genericApiErrorMessage();
        }
    }

    genericApiErrorMessage(): string {
        return this.translate('There was an error processing your request');
    }
}
